import React from 'react';
import { SpreadsheetData } from '../types';

interface UseSpreadsheetDimensionsProps {
  columns: number;
  rows: number;
  data: SpreadsheetData[];
  autoFillSpace: boolean;
  minColumns: number;
  minRows: number;
  columnWidths?: number[];
  rowHeight: number;
  showRowNumbers: boolean;
  showColumnHeaders: boolean;
  fillHeight: boolean;
  isFullscreen: boolean;
  buffers: { fullscreen?: number; normal?: number };
  maxHeight?: number;
}

export function useSpreadsheetDimensions({
  columns,
  rows,
  data,
  autoFillSpace,
  minColumns,
  minRows,
  columnWidths,
  rowHeight,
  showRowNumbers,
  showColumnHeaders,
  fillHeight,
  isFullscreen,
  buffers,
  maxHeight
}: UseSpreadsheetDimensionsProps) {
  // State for auto-calculated dimensions
  const [calculatedColumns, setCalculatedColumns] = React.useState(columns);
  const [calculatedRows, setCalculatedRows] = React.useState(rows);
  
  // State for dynamic height calculation
  const [dynamicHeight, setDynamicHeight] = React.useState<number | null>(null);

  // Container ref for dimension calculations
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Calculate optimal dimensions based on container size
  const calculateOptimalDimensions = React.useCallback((containerWidth: number, containerHeight: number) => {
    if (!autoFillSpace) {
      setCalculatedColumns(columns);
      setCalculatedRows(rows);
      return;
    }

    // Calculate available space
    const rowNumberWidth = showRowNumbers ? 50 : 0;
    const availableWidth = containerWidth - rowNumberWidth - 2; // 2px for borders
    const headerHeight = showColumnHeaders ? 35 : 0;
    const availableHeight = containerHeight - headerHeight - 2; // 2px for borders

    // Calculate default column width (use first custom width or default 100)
    const defaultColumnWidth = columnWidths?.[0] || 100;
    
    // Calculate how many columns and rows can fit
    const maxPossibleColumns = Math.floor(availableWidth / defaultColumnWidth);
    const maxPossibleRows = Math.floor(availableHeight / rowHeight);

    // Ensure we show at least the minimum required content
    const neededColumns = Math.max(minColumns, data.length > 0 ? 
      Math.max(...data.map(row => Object.keys(row).filter(key => key !== '__rowNumber').length)) : columns);
    const neededRows = Math.max(minRows, Math.max(data.length, rows));

    // Final dimensions: max of needed and what fits, but at least the needed amount
    const finalColumns = Math.max(neededColumns, Math.min(maxPossibleColumns, neededColumns + 5)); // Add some extra columns for usability
    const finalRows = Math.max(neededRows, Math.min(maxPossibleRows, neededRows + 10)); // Add some extra rows for usability

    setCalculatedColumns(finalColumns);
    setCalculatedRows(finalRows);
  }, [autoFillSpace, columns, rows, columnWidths, rowHeight, showRowNumbers, showColumnHeaders, data, minColumns, minRows]);

  // Calculate height based on fillHeight prop
  const getSpreadsheetHeight = React.useCallback(() => {
    const currentRows = autoFillSpace ? calculatedRows : rows;
    
    // Use dynamic height if available (for fillHeight/fullscreen modes)
    if ((fillHeight || isFullscreen) && dynamicHeight !== null) {
      return dynamicHeight;
    }
    
    if (maxHeight) {
      return Math.min(maxHeight, (currentRows * rowHeight) + (showColumnHeaders ? 35 : 0) + 2);
    }
    
    return (currentRows * rowHeight) + (showColumnHeaders ? 35 : 0) + 2;
  }, [autoFillSpace, calculatedRows, rows, fillHeight, isFullscreen, dynamicHeight, maxHeight, rowHeight, showColumnHeaders]);

  // ResizeObserver to watch container size changes
  React.useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      if (!containerRef.current) return;

      const { width, height } = containerRef.current.getBoundingClientRect();
      
      // Handle dynamic height calculation for fullscreen/fillHeight mode
      if (fillHeight || isFullscreen) {
        const rect = containerRef.current.getBoundingClientRect();
        const distanceFromTop = rect.top;
        const calcBuffer = isFullscreen ? buffers.fullscreen || 0 : buffers.normal || 0;
        const availableHeight = window.innerHeight - distanceFromTop - calcBuffer;
        
        // Set a minimum height to prevent collapse
        const minHeight = isFullscreen ? 100 : 300;
        setDynamicHeight(Math.max(availableHeight, minHeight));
      }
      
      // Handle auto-fill space calculations
      if (autoFillSpace) {
        calculateOptimalDimensions(width, height);
      }
    };

    updateDimensions();

    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.current);
    
    // Also listen to window resize for viewport changes
    window.addEventListener('resize', updateDimensions);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateDimensions);
    };
  }, [calculateOptimalDimensions, autoFillSpace, fillHeight, isFullscreen, buffers]);

  return {
    containerRef,
    calculatedColumns,
    calculatedRows,
    dynamicHeight,
    getSpreadsheetHeight
  };
} 